<template>
  <!-- prettier-ignore -->
  <div class="products">
    <basic-text-input
      ref="inputRef"
      v-model="selectedLabel"
      manual-validation
      name="products"
      :disabled="$props.disabled"
      :label="$props.label"
      :size="$props.size"
      :supporting-text="$props.supportingText"
      :v="$props.v"
      @clear="doClear"
      @keydown="onKeyDown"
      @input.stop="search"
    >
      <template #trailing>
        <the-icon
          v-if="busy"
          class="fa-spin"
          art="duo"
          name="spinner-third"
        />
      </template>
    </basic-text-input>

    <basic-dropdown-menu
      :id="dropdownId"
      ref="dropdownRef"
      manual-focus
      name="products-dropdown"
      :options="results"
      :selected="model"
      :size="$props.size"
      @close="onClose"
      @select="selectItem"
    />
  </div>
</template>

<script setup>
  import { computed, nextTick, reactive, ref } from 'vue'
  import { v4 as uuidv4 } from 'uuid'

  import useI18n from '@shared/hooks/useI18n'

  import { ReactivityUtil } from '@shared/utils/Reactivity'

  import BasicDropdownMenu from '@shared/components/Basic/DropdownMenu'
  import BasicTextInput from '@shared/components/Basic/TextInput'

  // HOOKS
  const { t } = useI18n()

  // INIT
  const emit = defineEmits(['update'])
  const model = defineModel({ type: [Object] })
  const props = defineProps({
    disabled: {
      type: Boolean,
      default: false,
    },

    hasTrailing: {
      type: Boolean,
      default: false,
    },

    label: {
      type: String,
      required: true,
    },

    size: {
      validator(value) {
        return ['sm', 'md'].includes(value)
      },
      default: 'sm',
    },

    supportingText: {
      type: String,
      default: undefined,
    },

    v: {
      type: Object,
      default: () => {},
    },

    value: {
      type: Object,
      default: () => {},
    },
  })

  // DATA
  const KEY = uuidv4()
  const dropdownId = `dropdown-${KEY}`
  const dropdownRef = ref(null)
  const inputRef = ref(null)
  const selectedLabel = ref(null)
  const results = reactive([])
  const busy = ref(false)

  // COMPUTED
  const productsList = computed(() => {
    const products = t('products')
    if (products) {
      return Object.entries(products).map(([key, value]) => ({
        key,
        displayName: value.designation,
      }))
    }
    return []
  })

  // METHODS
  function doClear() {
    model.value = null
  }

  /**
   * when the dropdown gets closed and there's no valid model, clear the selectedLabel/input
   */
  async function onClose() {
    await nextTick()

    if (!model.value) {
      selectedLabel.value = null
    }
  }

  /**
   * handle keydown-events to set focus to opened dropdown menu
   */
  function onKeyDown(event) {
    if (dropdownRef.value.isOpen) {
      switch (event.code) {
        case 'Enter':
        case 'NumpadEnter':
          selectItem(results[0])
          dropdownRef.value.close()
          event.preventDefault()
          break

        case 'Escape':
          dropdownRef.value.close()
          event.preventDefault()
          break

        case 'Tab':
        case 'ArrowUp':
        case 'ArrowDown':
          dropdownRef.value.setFocus()
          event.preventDefault()
          break
      }
    }
  }

  /**
   * select item and then set focus on the input-field (to restore tabbing order)
   */
  function selectItem(item) {
    if (item.value.key) {
      emit('update', item.value.key)
      selectedLabel.value = null
    }
  }

  /**
   * trigger search with input and if there're results, open dropdown menu
   */
  function search(event) {
    model.value = null
    props.v.$reset()

    const query = event.target.value.replace(/-+/g, '-')
    if (query.length > 1) {
      busy.value = true
      const products = searchProducts(query)
      busy.value = false
      ReactivityUtil.reAssign(results, products)

      // has results => open menu
      if (results.length > 0) {
        dropdownRef.value.open()

      // no results => close menu as mark as validate
      } else {
        dropdownRef.value.close()

        if (props.v?.$validate) {
          props.v.$validate()
        }
      }
    }
  }

  function searchProducts(searchQuery) {
    const products = productsList.value.filter(product => {
      if (product.displayName) {
        return product.displayName?.toLowerCase().includes(searchQuery.toLowerCase())
      }
      return false
    })

    // no results
    if (products.length === 0) {
      model.value = null
    }

    return products.map(product => {
      return {
        value: product,
        label: product.displayName,
      }
    })
  }
</script>

<style scoped>
.products {
  position: relative;

  .icon {
    color: var(--on-surface);
  }
}
</style>
